import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { LoadingScreen } from '@/components/ui';
import { initSessionChannel, closeSessionChannel } from '@/utils/broadcastChannel';

interface AuthProviderProps {
  children: React.ReactNode;
}

// WP-BUG-61: Module-level flag survives component unmount/remount within a
// single page session. Resets on full page reload (which is correct — we DO
// want to check auth after a reload). A useRef would reset if a parent
// re-render causes AuthProvider to remount, triggering a second checkAuth()
// whose stale 401 overwrites isAuthenticated:true set by OAuth login.
let authProviderInitialized = false;

// Whether the INITIAL checkAuth() has settled. Separate from the store's
// `isLoading`, and that separation is the whole point — see the note on the
// render guard below. Module-level for the same reason as the flag above.
let initialAuthCheckSettled = false;

// The initial check itself, so that a later effect run - or a remount that
// happens while it is still in flight - can subscribe to the same promise
// rather than starting a second check or waiting on nothing.
let initialAuthCheck: Promise<void> | null = null;

/**
 * AuthProvider - Wraps the app to handle authentication initialization.
 * WP-AUTH-12: httpOnly Cookie for Refresh Token Persistence
 *
 * On mount:
 * 1. Attempts to refresh the session using httpOnly cookie
 * 2. Initializes cross-tab session coordination via BroadcastChannel
 *
 * Shows loading screen until auth check completes to prevent
 * flash redirects to login page.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { checkAuth } = useAuthStore();
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(initialAuthCheckSettled);

  useEffect(() => {
    // WP-BUG-61: Module-level guard prevents re-initialization on remount
    if (!authProviderInitialized) {
      authProviderInitialized = true;

      // Initialize cross-tab session coordination
      // When another tab logs out, this tab should also logout
      initSessionChannel(() => {
        // Clear local state and redirect to login
        // Don't call logout() again - just clear state
        useAuthStore.setState({
          user: null,
          userId: null,
          userEmail: null,
          userName: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          mfaRequired: false,
          mfaToken: null,
        });
        navigate('/login', { replace: true });
      });

      // Check authentication status on app load
      // This will attempt to refresh the session using the httpOnly cookie
      initialAuthCheck = checkAuth()
        // checkAuth handles its own failures internally, but `.finally()`
        // re-throws whatever it was given - so without this, an unexpected
        // rejection would escape as an unhandled promise rejection. A failed
        // session check is the ordinary "not logged in" case; the router
        // decides what happens next.
        .catch(() => undefined)
        .then(() => {
          initialAuthCheckSettled = true;
        });
    }

    // Subscribe to the in-flight check on EVERY run, not only the one that
    // started it.
    //
    // This effect re-runs whenever `navigate`'s identity changes, which happens
    // on navigation. The first version cancelled its pending callback in the
    // cleanup and then hit the `authProviderInitialized` guard on the re-run,
    // so nothing was left to lift the gate: the app sat on
    // "Checking authentication..." forever. Playwright caught it immediately -
    // auth.setup.ts could not find the sign-in heading, and its page snapshot
    // was that spinner and nothing else.
    //
    // Re-subscribing makes the latest run the live one, and covers the harder
    // case too: an AuthProvider that unmounts and remounts while the initial
    // check is still in flight gets its own subscription rather than depending
    // on a closure that belongs to a dead component.
    let active = true;
    void (initialAuthCheck ?? Promise.resolve()).then(() => {
      if (active) setAuthChecked(true);
    });

    return () => {
      active = false;
    };
  }, [checkAuth, navigate]);

  // Close the broadcast channel on unmount ONLY.
  //
  // It used to be cleaned up by the effect above, whose cleanup also runs on
  // every dependency change - and the re-run then hit the initialization guard
  // and never reopened it. So a single change of `navigate` identity left
  // cross-tab logout silently dead for the rest of the session. Tying this to
  // an empty dependency list makes "on unmount" mean what it says.
  useEffect(() => closeSessionChannel, []);

  // Gate on the INITIAL auth check only - never on the store's `isLoading`.
  //
  // `isLoading` is shared by every auth action: login, register, verifyMfa,
  // magic link. Gating on it meant that pressing "Sign in" unmounted the whole
  // app - the login form included - and replaced it with a full-screen
  // "Checking authentication...", which is both the wrong message and the wrong
  // moment. On a slow connection the user watched it for the length of the
  // request; on a fast one it was a flash of the wrong thing.
  //
  // It also made the button's own busy state unobservable. Button sets
  // aria-busy for WCAG 4.1.3 (Button.tsx:53), but a screen reader cannot
  // announce a control that has just been unmounted. That is how this was
  // found: accessibility.spec.ts "loading states are announced" clicks Sign in
  // with the login response held open and asserts aria-busy on the button. The
  // button was gone, and the page snapshot at failure showed exactly one thing:
  // "Checking authentication...".
  //
  // The original intent - no flash redirect to /login before the refresh call
  // has answered - is preserved, because that is precisely what this flag
  // tracks.
  if (!authChecked) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  return <>{children}</>;
}

export default AuthProvider;
